/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../IEditor";
import { IEditorApiService } from "../api/IEditorApiService";
import {
	createEventEmitter,
	emitEvent,
	isDeepEqual,
	removeAllEventListeners,
	TFrozenType
} from "@adapptio/lib-shared";
import { IBlueprintContentMetadata, IEditorApiConfigResponse } from "@adapptio/lib-core";

const REFRESH_RATE = 60000;

export class AppConfigService {
	private readonly apiService: IEditorApiService;
	private appConfig: TFrozenType<IEditorApiConfigResponse>;
	private manifest: IBlueprintContentMetadata | null = null;
	private refreshTimeoutId: ReturnType<typeof setTimeout>;

	public readonly onChange = createEventEmitter<void>();

	constructor(dc: IEditor) {
		this.apiService = dc.get("apiService");
	}

	public async init(): Promise<void> {
		await this.loadManifest();
		this.startDataRefresh();
		return;
	}

	public destroy(): void {
		this.clearRefreshTimeout();
		removeAllEventListeners(this.onChange);
	}

	private async loadManifest(): Promise<void> {
		this.manifest = await this.apiService.getManifest();
		return;
	}

	public getManifest(): IBlueprintContentMetadata {
		return this.manifest;
	}

	public async updateManifest(content: string, sourceRevision = this.manifest.revision): Promise<void> {
		await this.apiService.updateManifest(content, sourceRevision);
		await this.startDataRefresh();
		this.loadManifest();

		return;
	}

	private clearRefreshTimeout(): void {
		if (this.refreshTimeoutId) {
			clearTimeout(this.refreshTimeoutId);
			this.refreshTimeoutId = null;
		}
	}

	private async startDataRefresh(): Promise<void> {
		this.clearRefreshTimeout();

		try {
			await this.loadAppConfig();
		} finally {
			this.refreshTimeoutId = setTimeout(() => {
				this.startDataRefresh();
			}, REFRESH_RATE);
		}
	}

	private async loadAppConfig(): Promise<void> {
		try {
			const config = await this.apiService.getAppConfig();
			this.setAppConfig(config);
		} catch (err) {
			console.error("Error while loading app config", err);
		}
	}

	private setAppConfig(newConfig: IEditorApiConfigResponse) {
		if (isDeepEqual(this.appConfig, newConfig)) {
			return;
		}

		this.appConfig = newConfig;
		this.emitChange();
	}

	private emitChange(): void {
		emitEvent(this.onChange);
	}

	public getConfig(): TFrozenType<IEditorApiConfigResponse> {
		return this.appConfig;
	}
}
